import { Box, Button, Container, Divider, Flex, Heading, Text } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { format, isEqual, startOfDay } from 'date-fns';
import { isBefore } from 'date-fns/fp';
import { dateInput } from 'helpers/makeFormFields';
import useFreeTrialMutation from 'hooks/mutations/useFreeTrialMuation';
import useWorkshopQuery from 'hooks/useWorkshopQuery';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

export interface IFreeTrialForm {
  trial_ends_at: string;
}

const FreeTrailDetailsCard = ({ workshopId }: { workshopId: number }) => {
  const { t } = useTranslation();

  const workshopQuery = useWorkshopQuery(workshopId);

  const freeTrialMutation = useFreeTrialMutation({ workshop_id: workshopId });
  // @ts-expect-error
  const endsAt: string = workshopQuery.data?.free_trial_ends_at;
  const formattedEndsAt = format(new Date(endsAt), 'dd.MM.yyyy');

  const schema = yup
    .object({
      trial_ends_at: yup.date().required(),
    })
    .required();

  const today = new Date();
  const endsAtDate = new Date(endsAt);
  const defaultTrialEndsAt = isBefore(today, endsAtDate) || isEqual(endsAtDate, today) ? today : endsAtDate;

  const defaultValues = {
    trial_ends_at: format(defaultTrialEndsAt, 'yyyy-MM-dd'),
  };

  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
  } = useForm<IFreeTrialForm>({
    // @ts-expect-error
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const onSubmit = (data: IFreeTrialForm) => {
    const submissionData = { trial_ends_at: format(startOfDay(new Date(data.trial_ends_at)), 'yyyy-MM-dd HH:mm:ss') };
    freeTrialMutation.mutate(submissionData);
  };

  return (
    <Container
      as="form"
      // @ts-expect-error
      onSubmit={handleSubmit(onSubmit)}
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      boxShadow="md"
      maxWidth={'unset'}
      p={4}
    >
      <Heading as="h2" size="xs" mb={4} width={'100%'}>
        {t('workshop:free_trial.header')}
      </Heading>
      <Divider mb={4} />
      <Flex w="full" justifyContent={'space-between'}>
        <Flex w="full" justifyContent={'space-between'} direction={'column'}>
          {endsAt !== null && (
            <Flex mb={10}>
              {workshopQuery?.data?.in_free_trial ? (
                <Flex>
                  <Text fontWeight={'thin'} mr={2}>
                    {t('workshop:free_trial.ends_at')}:
                  </Text>
                  <Text fontWeight={'semibold'}>{formattedEndsAt}</Text>
                </Flex>
              ) : (
                <Flex>
                  <Text fontWeight={'thin'} mr={2}>
                    {t('workshop:free_trial.ended_at')}:
                  </Text>
                  <Text fontWeight={'semibold'}>{formattedEndsAt}</Text>
                </Flex>
              )}
            </Flex>
          )}

          <Box width={'320px'}>
            {dateInput({
              name: 'trial_ends_at',
              label: undefined,
              control,
              errors,
              schema,
              description: t('workshop:free_trial.description'),
              minDate: defaultTrialEndsAt,
            })}
          </Box>
        </Flex>
        <Flex alignItems={'flex-end'}>
          <Button variant="primary" type="submit" disabled={!isDirty || freeTrialMutation.isPending} isLoading={freeTrialMutation.isPending}>
            {t('common:save')}
          </Button>
        </Flex>
      </Flex>
    </Container>
  );
};

export default FreeTrailDetailsCard;
